import { ThunkDispatch } from "core-integration/src/store";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { IEvent } from "core-integration/src/store/events/types";
import {
  getPlayerData,
  getPlayerUpdateError,
  getRegisterError,
} from "core-integration/src/store/player/reducers";
import {
  playerUpdate,
  register,
} from "core-integration/src/store/player/thunks";
import { getRegions } from "core-integration/src/store/regions/reducers";
import { fetchRegions } from "core-integration/src/store/regions/thunks";
import range from "lodash/range";
import sortBy from "lodash/sortBy";
import values from "lodash/values";
import moment from "moment";
import { size } from "polished";
import React, { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import NbaIDLogo from "../../img/nba-id-logo.svg?react";
import { IPlayer } from "../../types";
import { getSite } from "../../utils/sites";
import {
  createCustomInteractionData,
  createInteractionData,
} from "../../utils/tealium";
import Alert from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import {
  CheckboxField,
  FieldFeedback,
  InputField,
  SelectField,
} from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";

const SaveBar = styled.div`
  margin-bottom: ${(props) => props.theme.space[4]};
  padding: 0 20% ${(props) => props.theme.space[4]};
  border-bottom: 3px solid #bfbfbf;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    margin-right: 10%;
    margin-left: 10%;
  }
`;

const DOBFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
`;

const DOBFieldsetInner = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: ${({ theme }) => theme.space[2]};
    margin-left: -${({ theme }) => theme.space[2]};
    margin-right: -${({ theme }) => theme.space[2]};
  }
`;

const DOBLegend = styled.legend`
  display: block;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.space[1]};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
`;

const DOBCol = styled.div`
  margin-top: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: calc(100% / 3);
    margin-top: 0;
    padding-left: ${({ theme }) => theme.space[2]};
    padding-right: ${({ theme }) => theme.space[2]};
  }
`;

const TitleStyle = styled.h2`
  margin-top: ${({ theme }) => theme.space[2]};
  margin-bottom: ${({ theme }) => theme.space[3]};
  font-family: ${(props) => props.theme.fonts.action};
  font-size: 4rem;
  font-style: italic;
  text-transform: capitalize;
`;

const StyledNBAIdLogo = styled(NbaIDLogo)`
  ${size(45)};
`;

type Props = WithTranslation;

interface IState {
  birthDay: number;
  birthMonth: number;
  birthYear: number;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  errorEmail: string;
  errorFName: string;
  errorLName: string;
  errorPass: string;
  region: string;
  emailNba: boolean;
  privacyNba: boolean;
  errorRegion: string;
  formDisabled: boolean;
  errorDob: string;
}

const Register: React.FC<Props> = ({ t }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch>();

  const player = useSelector(getPlayerData) as IPlayer | null;
  const registerError = useSelector(getRegisterError);
  const updateError = useSelector(getPlayerUpdateError);
  const regions = useSelector(getRegions);
  const nextEvent = useSelector(getNextEvent);

  const doBootstrap = () => dispatch(bootstrap());
  const registerPlayer = (data: any) => dispatch(register(data));
  const updatePlayer = (data: any) => dispatch(playerUpdate(data));
  const fetchRegionData = () => dispatch(fetchRegions());

  const errorsTxt: { [key: string]: string } = {
    EDob: t("register.errors.dobn", "Date of birth not valid"),
    EEmail: t("register.errors.email", "An email is required"),
    EFName: t("register.errors.fname", "First name is required"),
    ELName: t("register.errors.lname", "Last name is required"),
    EPass: t("register.errors.pass", "A password is required"),
    EPassLength: t(
      "register.errors.passLength",
      "Password must be at least 8 characters long and include 1 capital letter and 1 symbol or number",
    ),
    EValidEmail: t(
      "register.errors.validEmail",
      "This Email address is already registered",
    ),
    EValidPass: t("register.errors.validPass", "Password not valid"),
    ERegion: t("register.errors.region", "A region is required"),
    EInvalid: t(
      "register.errors.invalid",
      "There was an error with your registration. If you already have an account, please try to log in",
    ),
    EInvalidDob: t(
      "register.errors.invalidDob",
      "The game is only available to individuals aged 16 and over",
    ),
    EInvalidEmail: t(
      "register.errors.invalidEmail",
      "Email address is already taken. If you already have an account, please try to log in",
    ),
    EInvalidPassword: t(
      "register.errors.invalidPassword",
      "Password must be at least 8 characters long and include 1 capital letter and 1 symbol or number.",
    ),
    EInvalidFname: t(
      "register.errors.invalidFname",
      "First Name must be provided",
    ),
    EInvalidLname: t(
      "register.errors.invalidLname",
      "Last Name must be provided",
    ),
    EInvalidRegion: t(
      "register.errors.invalidRegion",
      "The region selected is invalid",
    ),
    EInvalidEconsent: t(
      "register.errors.invalidEconsent",
      "NBA Email consent invalid",
    ),
    EInvalidPconsent: t(
      "register.errors.invalidPconsent",
      "NBA Privacy consent invalid",
    ),
  };

  const months: { [key: string]: string } = {
    1: t("months.janShort", "Jan"),
    2: t("months.febShort", "Feb"),
    3: t("months.marShort", "Mar"),
    4: t("months.aprShort", "Apr"),
    5: t("months.mayShort", "May"),
    6: t("months.junShort", "Jun"),
    7: t("months.julShort", "Jul"),
    8: t("months.augShort", "Aug"),
    9: t("months.sepShort", "Sep"),
    10: t("months.octShort", "Oct"),
    11: t("months.novShort", "Nov"),
    12: t("months.decShort", "Dec"),
  };

  const [state, setState] = useState<IState>({
    birthDay:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[2])
        : 0,
    birthMonth:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[1])
        : 0,
    birthYear:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[0])
        : 0,
    email:
      player && player.email.indexOf("@ismfgsocial") === -1 ? player.email : "",
    errorDob: "",
    errorEmail: "",
    errorFName: "",
    errorLName: "",
    errorPass: "",
    firstName: player ? player.first_name : "",
    lastName: player ? player.last_name : "",
    password: "",
    emailNba: player && player.nba_email ? true : false,
    privacyNba: player ? true : false,
    errorRegion: "",
    region: player ? player.region : "",
    formDisabled: false,
  });

  const getMode = () => {
    if (!player) {
      return "register";
    }
    return player.dirty ? "confirm" : "update";
  };

  const getRequiredFields: () => Array<keyof IState> = () => {
    switch (getMode()) {
      case "register":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "password",
          "region",
          "privacyNba",
        ];
      case "confirm":
        return ["firstName", "lastName"];
      case "update":
        return [];
    }
  };

  const apiDataFromState = () => {
    switch (getMode()) {
      case "register":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          first_name: state.firstName,
          last_name: state.lastName,
          password: state.password,
          region: state.region,
          nba_email: state.emailNba ? "1" : "0",
          nba_privacy: state.privacyNba ? "1" : "0",
        };
      case "confirm":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          first_name: state.firstName,
          last_name: state.lastName,
          password: state.password,
          region: state.region,
          nba_email: state.emailNba,
          nba_privacy: state.privacyNba,
        };
      case "update":
        return {};
    }
  };

  const handleBirthDayChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthDay: parseInt(e.currentTarget.value, 10) });

  const handleBirthMonthChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthMonth: parseInt(e.currentTarget.value, 10) });

  const handleBirthYearChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthYear: parseInt(e.currentTarget.value, 10) });

  const handleEmailChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, email: e.currentTarget.value, formDisabled: false });

  const handleFirstNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, firstName: e.currentTarget.value });

  const handleLastNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, lastName: e.currentTarget.value });

  const handlePasswordChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      password: e.currentTarget.value,
      formDisabled: false,
    });

  const handleRegionChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, region: e.currentTarget.value });

  const handleEmailNbaChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, emailNba: e.currentTarget.checked ? true : false });

  const handleNbaPrivacyChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, privacyNba: e.currentTarget.checked ? true : false });

  const handleSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>,
    nextEvent: IEvent | null,
  ) => {
    e.preventDefault();
    const mode = getMode();
    setState({ ...state, formDisabled: true });
    if (mode === "register") {
      try {
        createInteractionData({
          contentTitle: "home",
          interactionId: "nba-fantasy-game:sign-up:cta",
          interactionText: t("register.label.register", "Register"),
        });
        await registerPlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        const errors = handleServerErrors();
        createCustomInteractionData({
          contentTitle: "home",
          interactionId: "fantasy:sign-up:error",
          ampEventName: "User Account: Registration Error",
          errorDetail: values(errors)
            .map((error) => error.errorDetail)
            .join(", "),
        });
        return;
      }
      await doBootstrap();
      const userData = {
        userId: player?.nbaciamguid,
        userState: "authenticated",
      };
      createCustomInteractionData({
        contentTitle: "home",
        interactionId: "fantasy:sign-up:success",
        userData: userData,
        ampEventName: "User Account: Registration Success",
      });
      if (nextEvent) {
        navigate("/squad-selection");
      } else {
        navigate("/update");
      }
    } else if (mode === "confirm") {
      try {
        await updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();
      if (nextEvent) {
        navigate("/squad-selection");
      } else {
        navigate("/update");
      }
    } else if (mode === "update") {
      await doBootstrap();
      // Maybe this should be back to my team, maybe our LoggedIn component
      if (nextEvent) {
        navigate("/squad-selection");
      } else {
        navigate("/update");
      }
    }
    setState({ ...state, formDisabled: false });
  };

  useEffect(() => {
    fetchRegionData();
  }, []);

  const getIntRegions = () => {
    const regs = regions;
    return sortBy(regs, ["name"]);
  };

  const handleFirstnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorFName: errorsTxt.EFName });
    } else {
      setState({ ...state, errorFName: "" });
    }
  };
  const validatePassword = (pw: string) => {
    return (
      /[A-Z]/.test(pw) &&
      (/[0-9]/.test(pw) || /[^A-Za-z0-9]/.test(pw)) &&
      pw.length > 7
    );
  };

  const handleLastnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorLName: errorsTxt.ELName });
    } else {
      setState({ ...state, errorLName: "" });
    }
  };
  const handlePasswordBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorPass: errorsTxt.EPass });
    } else {
      if (!validatePassword(e.currentTarget.value)) {
        setState({ ...state, errorPass: errorsTxt.EPassLength });
      } else {
        setState({ ...state, errorPass: "" });
      }
    }
  };

  const handleEmailBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorEmail: errorsTxt.EEmail });
    } else {
      setState({ ...state, errorEmail: "" });
    }
  };

  const handleRegionBlur = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    if (!e.currentTarget.value || e.currentTarget.value === "0") {
      setState({ ...state, errorRegion: errorsTxt.ERegion });
    } else {
      setState({ ...state, errorRegion: "" });
    }
  };
  const ageLimit = () => {
    const site = getSite();
    interface Dictionary {
      [key: string]: number;
    }
    const ageLimitDefaults: Dictionary = {
      default: 16,
      main_ar: 16,
      main_he: 16,
      main_tr: 16,
      main_en: 18,
      main_fr: 18,
      main_de: 18,
      main_es: 18,
      main_it: 18,
      main_el: 18,
      main_br: 18,
      lequipe: 18,
    };
    const ageLimit =
      site in ageLimitDefaults
        ? ageLimitDefaults[site.toLowerCase()]
        : ageLimitDefaults["default"];
    return ageLimit;
  };

  const handleDobBlur = (_: React.SyntheticEvent<HTMLSelectElement>) => {
    const year = state.birthYear;
    const month = state.birthMonth;
    const day = state.birthDay;
    const ageLim = ageLimit();
    if (year > 0 && month > 0 && day > 0) {
      if (moment(new Date(year, month - 1, day)).isValid()) {
        const today = new Date();
        const birthDate = new Date(year, month - 1, day);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < ageLim) {
          setState({ ...state, errorDob: errorsTxt.EDob });
          setState({ ...state, formDisabled: true });
        } else {
          setState({ ...state, errorDob: "" });
          setState({ ...state, formDisabled: false });
        }
      } else {
        setState({ ...state, errorDob: errorsTxt.EDob });
        setState({ ...state, formDisabled: true });
      }
    } else {
      setState({ ...state, formDisabled: true });
    }
  };

  const handleServerErrors = () => {
    const errors = getMode() === "register" ? registerError : updateError;
    const formattedErrors = [];
    if (errors) {
      if (
        errors.badRequest &&
        errors.badRequest.password &&
        errors.badRequest.password[0].code === "invalid"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EValidPass,
          errorDetail: "password|Password not valid",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "duplicate"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EValidEmail,
          errorDetail: "email|Email already registered",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "blank"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EEmail,
          errorDetail: "email|Email is required",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.password &&
        errors.badRequest.password[0].code === "blank"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EPass,
          errorDetail: "password|Password is required",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.first_name &&
        errors.badRequest.first_name[0].code === "blank"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EFName,
          errorDetail: "first name|First name is required",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.ELName,
          errorDetail: "last name|Last name is required",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.region &&
        errors.badRequest.region[0].code === "does_not_exist"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.ERegion,
          errorDetail: "region|Region is required",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes("EMAIL_TAKEN")
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidEmail,
          errorDetail: "email address|Email address is already taken",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes(
          "INVALID_PASSWORD",
        )
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidPassword,
          errorDetail: "password|Incorrect format",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes(
          "INVALID_FIRST_NAME",
        )
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidFname,
          errorDetail: "first name|First name must be provided",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes(
          "INVALID_LAST_NAME",
        )
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidLname,
          errorDetail: "last name|Last name must be provided",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes(
          "INVALID_COUNTRY",
        )
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidRegion,
          errorDetail: "region|Region selected is invalid",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes(
          "INVALID_EMAIL_CONSENT",
        )
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidEconsent,
          errorDetail: "consent|NBA Email consent invalid",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "invalid" &&
        errors.badRequest.non_field_errors[0].message.includes(
          "INVALID_PRIVACY_CONSENT",
        )
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidPconsent,
          errorDetail: "consent|NBA Privacy consent invalid",
        });
      }
      if (
        errors.badRequest &&
        errors.badRequest.non_field_errors &&
        errors.badRequest.non_field_errors[0].code === "under_age"
      ) {
        formattedErrors.push({
          errorText: errorsTxt.EInvalidDob,
          errorDetail: "date of birth|Underage",
        });
      }

      return formattedErrors;
    }
  };

  const isFormDisabled = () => {
    const requiredFieldsMissing = getRequiredFields().some((e) => !state[e]);
    return requiredFieldsMissing || state.formDisabled;
  };

  const mode = getMode();
  const title = {
    register: t(
      "register.title.registration",
      "Register your NBA fantasy account",
    ),
    confirm: t("register.title.confirm", "Confirm your details"),
    update: t("register.title.updaten", "Update your details"),
  };
  const buttonText = {
    register: t("register.label.register", "Register"),
    confirm: t("register.label.confirm", "Update"),
    update: t("register.label.update", "Update"),
  };
  const age = ageLimit();
  const errors = handleServerErrors();

  return (
    <Wrapper>
      <Main>
        <div>
          {(mode === "confirm" || mode === "register") && (
            <Box mx={2}>
              <StyledNBAIdLogo />

              <TitleStyle>{title[mode]}</TitleStyle>
              <p>
                {t(
                  "register.required",
                  "Fields marked with an asterisk (*) are required.",
                )}
              </p>
            </Box>
          )}
          {errors && (
            <Box my={4} mx={2}>
              <Alert type="error">
                {values(errors).map((r) => r.errorText)}
              </Alert>
            </Box>
          )}
          {(mode === "confirm" || mode === "register") && (
            <Copy>
              <form onSubmit={(e) => handleSubmit(e, nextEvent)}>
                {mode === "register" && (
                  <Box>
                    <InputField
                      required
                      id="ismRegisterEmail"
                      type="email"
                      hasErrors={!!state.errorEmail}
                      label={t("register.label.email", "Email")}
                      value={state.email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                    />
                    {state.errorEmail && (
                      <FieldFeedback>{state.errorEmail}</FieldFeedback>
                    )}
                  </Box>
                )}
                {mode === "register" && (
                  <Box mt={2}>
                    <InputField
                      required
                      id="ismRegisterPassword"
                      type="password"
                      hasErrors={!!state.errorPass}
                      label={t("register.label.password", "Password")}
                      value={state.password}
                      onChange={handlePasswordChange}
                      hint={t(
                        "register.hint.passsword",
                        "Must be at least 8 characters long and include 1 capital letter and 1 symbol or number.",
                      )}
                      onBlur={handlePasswordBlur}
                    />
                    {state.errorPass && (
                      <FieldFeedback>{state.errorPass}</FieldFeedback>
                    )}
                  </Box>
                )}

                <Box mt={2}>
                  <InputField
                    required
                    id="ismRegisterFirstName"
                    hasErrors={!!state.errorFName}
                    label={t("register.label.fName", "First Name")}
                    value={state.firstName}
                    onChange={handleFirstNameChange}
                    onBlur={handleFirstnameBlur}
                    maxLength={60}
                  />
                  {state.errorFName && (
                    <FieldFeedback>{state.errorFName}</FieldFeedback>
                  )}
                </Box>
                <Box mt={2}>
                  <InputField
                    required
                    id="ismRegisterLastName"
                    label={t("register.label.lName", "Last Name")}
                    hasErrors={!!state.errorLName}
                    value={state.lastName}
                    onChange={handleLastNameChange}
                    onBlur={handleLastnameBlur}
                    maxLength={60}
                  />
                  {state.errorLName && (
                    <FieldFeedback>{state.errorLName}</FieldFeedback>
                  )}
                </Box>
                {mode === "register" && (
                  <Box mt={2}>
                    {state.errorRegion && (
                      <FieldFeedback>{state.errorRegion}</FieldFeedback>
                    )}
                    <SelectField
                      required
                      value={state.region}
                      onChange={handleRegionChange}
                      label={t("register.label.regionn", "Region")}
                      id="ismRegisterRegion"
                      onBlur={handleRegionBlur}
                    >
                      <option value="" aria-selected={!state.region}>
                        {t("register.label.select", "Select")}
                      </option>
                      {values(getIntRegions()).map((r) => (
                        <option
                          value={r.iso_code_short}
                          aria-selected={r.iso_code_short === state.region}
                          key={r.id}
                        >
                          {r.name}
                        </option>
                      ))}
                    </SelectField>
                  </Box>
                )}
                {mode === "register" && (
                  <Box mt={2}>
                    <DOBFieldset>
                      <DOBLegend>
                        {t("register.label.dob", "Date of birth")}
                      </DOBLegend>
                      <Trans
                        i18nKey="register.hint.dob"
                        values={{ age }}
                        defaults={
                          "The game is only available to individuals aged {{ age }} and over"
                        }
                      />
                      <DOBFieldsetInner>
                        <DOBCol>
                          <SelectField
                            labelVisibility={false}
                            id="ismRegisterBirthDay"
                            value={state.birthDay}
                            onChange={handleBirthDayChange}
                            label={t("register.label.day", "Day")}
                            onBlur={handleDobBlur}
                          >
                            <option
                              value="0"
                              aria-selected={!state.birthDay}
                              disabled
                              defaultValue="0"
                            >
                              {t("register.label.day", "Day")}
                            </option>
                            {range(1, 32).map((d) => (
                              <option
                                value={d}
                                aria-selected={d === state.birthDay}
                                key={d}
                              >
                                {d}
                              </option>
                            ))}
                          </SelectField>
                        </DOBCol>
                        <DOBCol>
                          <SelectField
                            labelVisibility={false}
                            id="ismRegisterBirthMonth"
                            value={state.birthMonth}
                            onChange={handleBirthMonthChange}
                            label={t("register.label.month", "Month")}
                            onBlur={handleDobBlur}
                          >
                            <option
                              value="0"
                              aria-selected={!state.birthMonth}
                              disabled
                              defaultValue="0"
                            >
                              {t("register.label.month", "Month")}
                            </option>
                            {Object.keys(months).map((m) => (
                              <option
                                value={m}
                                aria-selected={
                                  parseInt(m, 10) === state.birthMonth
                                }
                                key={m}
                              >
                                {months[m]}
                              </option>
                            ))}
                          </SelectField>
                        </DOBCol>
                        <DOBCol>
                          <SelectField
                            labelVisibility={false}
                            value={state.birthYear}
                            onChange={handleBirthYearChange}
                            label={t("register.label.year", "Year")}
                            id="ismRegisterBirthYear"
                            onBlur={handleDobBlur}
                          >
                            <option
                              value="0"
                              aria-selected={!state.birthYear}
                              disabled
                              defaultValue="0"
                            >
                              {t("register.label.year", "Year")}
                            </option>
                            {range(
                              new Date().getFullYear() - ageLimit(),
                              1900,
                            ).map((y) => (
                              <option
                                value={y}
                                aria-selected={y === state.birthYear}
                                key={y}
                              >
                                {y}
                              </option>
                            ))}
                          </SelectField>
                        </DOBCol>
                      </DOBFieldsetInner>
                      {state.errorDob && (
                        <FieldFeedback>{state.errorDob}</FieldFeedback>
                      )}
                    </DOBFieldset>
                  </Box>
                )}
                {mode === "register" && (
                  <Box mt={2}>
                    <CheckboxField
                      checked={state.emailNba}
                      id="ismRegisterEmailNba"
                      label={t(
                        "register.label.optinn",
                        "Please use my personal information for the NBA to send me messages and advertisements about products and initiatives of the NBA and NBA partners.",
                      )}
                      onChange={handleEmailNbaChange}
                    />
                    <CheckboxField
                      checked={state.privacyNba}
                      id="ismRegisterPrivacyNba"
                      label={
                        <>
                          <Trans i18nKey="register.label.privacyNba">
                            I agree to the &nbsp;
                            <a
                              href="http://www.nba.com/news/termsofuse/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Terms and Conditions
                            </a>
                            &nbsp; and&nbsp;
                            <a
                              href="https://www.nba.com/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Policy
                            </a>
                          </Trans>
                          *
                        </>
                      }
                      onChange={handleNbaPrivacyChange}
                    />
                  </Box>
                )}
                <Box my={2}>
                  <SaveBar>
                    <Button
                      type="submit"
                      disabled={isFormDisabled()}
                      fullwidth="true"
                    >
                      {buttonText[mode]}
                    </Button>
                  </SaveBar>
                </Box>
              </form>
              {mode === "register" && (
                <p>
                  <Trans
                    i18nKey="register.label.usDataProcessingConsent"
                    defaults="By agreeing to the above terms, you are consenting that your personal information will be collected, stored and processed in the United States on behalf of NBA Properties, Inc. Details about how to withdraw your consent are available in the <privacyLink>Privacy Policy</privacyLink>."
                    components={{
                      privacyLink: (
                        <a
                          href="https://www.nba.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  />
                </p>
              )}
            </Copy>
          )}
          {mode === "update" && (
            <Box mx={2}>
              <h2>{title[mode]}</h2>
              <p>
                <Trans i18nKey="register.label.updateDetailsNba">
                  To update your details and password please visit &nbsp;
                  <a
                    href="https://www.nba.com/account/nbaprofile"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.nba.com/account/nbaprofile
                  </a>
                </Trans>
              </p>
              <p>
                {t(
                  "register.updateDetailsNba.notice",
                  "Please note, your changes will only be available in the game once you log in again",
                )}
              </p>
            </Box>
          )}
        </div>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(Register);
